import React, { useState, useEffect } from "react";
import "./App.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataRateCalculator = () => {
  const [xSize, setXSize] = useState('1920');
  const [ySize, setYSize] = useState('1080');
  const [frameRate, setFrameRate] = useState('30');
  const [videoSource, setVideoSource] = useState('mono8');
  const [compression, setCompression] = useState('none');
  const [numSources, setNumSources] = useState('1');
  const [eventDuration, setEventDuration] = useState('3600');
  const [numEvents, setNumEvents] = useState('1');
  const [allowedFormFactors, setAllowedFormFactors] = useState(['U.2', 'U.3', 'M.2']);
  const [writeThreshold, setWriteThreshold] = useState(75);
  const [capacityUtilization, setCapacityUtilization] = useState(100);
  const [ssdRecommendations, setSsdRecommendations] = useState([]);
  const [recordingMbpsPerSource, setRecordingMbpsPerSource] = useState(0);
  const [recordingTotalStorageGb, setRecordingTotalStorageGb] = useState(0);
  const [usingCompressedForRecording, setUsingCompressedForRecording] = useState(false);
  const [results, setResults] = useState({
    individual: {
      uncompressed: {
        gbps: 0,
        mbps: 0,
        mbitsps: 0,
        storagePerMinute: 0,
        storagePerHour: 0,
        storagePerEvent: 0
      },
      compressed: {
        gbps: 0,
        mbps: 0,
        mbitsps: 0,
        storagePerMinute: 0,
        storagePerHour: 0,
        storagePerEvent: 0
      }
    },
    total: {
      uncompressed: {
        gbps: 0,
        mbps: 0,
        mbitsps: 0,
        storagePerMinute: 0,
        storagePerHour: 0,
        storagePerEvent: 0,
        storageAllEvents: 0
      },
      compressed: {
        gbps: 0,
        mbps: 0,
        mbitsps: 0,
        storagePerMinute: 0,
        storagePerHour: 0,
        storagePerEvent: 0,
        storageAllEvents: 0
      }
    }
  });

  // Common presets
  const presets = [
    { name: 'HD (1920×1080)', x: 1920, y: 1080 },
    { name: '4K UHD (3840×2160)', x: 3840, y: 2160 },
    { name: '8K UHD (7680×4320)', x: 7680, y: 4320 },
    { name: 'Custom', x: null, y: null }
  ];

  const videoSourceOptions = [
    { value: 'mono8',      label: 'Mono8',      bits: 8 },
    { value: 'mono10',     label: 'Mono10',     bits: 10 },
    { value: 'mono12',     label: 'Mono12',     bits: 12 },
    { value: 'rgb8',       label: 'RGB8',       bits: 24 },
    { value: 'bayer_rg8',  label: 'BayerRG8',   bits: 8 },
    { value: 'bayer_rg10', label: 'BayerRG10',  bits: 10 },
    { value: 'bayer_rg12', label: 'BayerRG12',  bits: 12 },
  ];

  const compressionOptions = [
    { value: 'none', label: 'None (Uncompressed)', ratio: 1 },
    { value: 'h264_low', label: 'H.264 Low (High Quality)', ratio: 20 },
    { value: 'h264_medium', label: 'H.264 Medium (Balanced)', ratio: 50 },
    { value: 'h264_high', label: 'H.264 High (Max Compression)', ratio: 100 }
  ];

  const durationPresets = [
    { name: '30 mins', value: 1800 },
    { name: '1 hour', value: 3600 },
    { name: '2 hours', value: 7200 },
    { name: '4 hours', value: 14400 },
    { name: '8 hours', value: 28800 },
    { name: '24 hours', value: 86400 }
  ];

  const calculateDataRate = () => {
    const x = parseInt(xSize) || 0;
    const y = parseInt(ySize) || 0;
    const fps = parseFloat(frameRate) || 0;
    const sources = parseInt(numSources) || 0;
    const duration = parseInt(eventDuration) || 0;
    const events = parseInt(numEvents) || 0;

    // Get bit depth from video source
    const sourceOption = videoSourceOptions.find(opt => opt.value === videoSource);
    const bits = sourceOption ? sourceOption.bits : 24;

    // Get compression ratio
    const compressionOption = compressionOptions.find(opt => opt.value === compression);
    const compressionRatio = compressionOption ? compressionOption.ratio : 1;

    if (x <= 0 || y <= 0 || fps <= 0 || bits <= 0 || sources <= 0) {
      // Reset results if invalid input
      setResults({
        individual: {
          uncompressed: { gbps: 0, mbps: 0, mbitsps: 0, storagePerMinute: 0, storagePerHour: 0, storagePerEvent: 0 },
          compressed: { gbps: 0, mbps: 0, mbitsps: 0, storagePerMinute: 0, storagePerHour: 0, storagePerEvent: 0 }
        },
        total: {
          uncompressed: { gbps: 0, mbps: 0, mbitsps: 0, storagePerMinute: 0, storagePerHour: 0, storagePerEvent: 0, storageAllEvents: 0 },
          compressed: { gbps: 0, mbps: 0, mbitsps: 0, storagePerMinute: 0, storagePerHour: 0, storagePerEvent: 0, storageAllEvents: 0 }
        }
      });
      return;
    }

    // Calculate UNCOMPRESSED data rate for individual source
    const totalPixelsPerSecond = x * y * fps;
    const totalBitsPerSecond = totalPixelsPerSecond * bits;
    const totalBytesPerSecond = totalBitsPerSecond / 8;
    
    // Convert to different units for individual source (UNCOMPRESSED)
    const individual_gbps = totalBytesPerSecond / (1024 * 1024 * 1024);
    const individual_mbps = totalBytesPerSecond / (1024 * 1024);
    const individual_mbitsps = totalBitsPerSecond / (1000 * 1000);
    
    // Storage calculations for individual source (UNCOMPRESSED)
    const individual_storagePerMinute = individual_gbps * 60;
    const individual_storagePerHour = individual_gbps * 3600;
    const individual_storagePerEvent = individual_gbps * duration;
    
    // Calculate COMPRESSED data rate for individual source
    const individual_compressed_gbps = individual_gbps / compressionRatio;
    const individual_compressed_mbps = individual_mbps / compressionRatio;
    const individual_compressed_mbitsps = individual_mbitsps / compressionRatio;
    const individual_compressed_storagePerMinute = individual_storagePerMinute / compressionRatio;
    const individual_compressed_storagePerHour = individual_storagePerHour / compressionRatio;
    const individual_compressed_storagePerEvent = individual_storagePerEvent / compressionRatio;
    
    // Calculate totals for all sources (UNCOMPRESSED)
    const total_gbps = individual_gbps * sources;
    const total_mbps = individual_mbps * sources;
    const total_mbitsps = individual_mbitsps * sources;
    const total_storagePerMinute = individual_storagePerMinute * sources;
    const total_storagePerHour = individual_storagePerHour * sources;
    const total_storagePerEvent = individual_storagePerEvent * sources;
    const total_storageAllEvents = total_storagePerEvent * events;

    // Calculate totals for all sources (COMPRESSED)
    const total_compressed_gbps = individual_compressed_gbps * sources;
    const total_compressed_mbps = individual_compressed_mbps * sources;
    const total_compressed_mbitsps = individual_compressed_mbitsps * sources;
    const total_compressed_storagePerMinute = individual_compressed_storagePerMinute * sources;
    const total_compressed_storagePerHour = individual_compressed_storagePerHour * sources;
    const total_compressed_storagePerEvent = individual_compressed_storagePerEvent * sources;
    const total_compressed_storageAllEvents = total_compressed_storagePerEvent * events;

    setResults({
      individual: {
        uncompressed: {
          gbps: individual_gbps,
          mbps: individual_mbps,
          mbitsps: individual_mbitsps,
          storagePerMinute: individual_storagePerMinute,
          storagePerHour: individual_storagePerHour,
          storagePerEvent: individual_storagePerEvent
        },
        compressed: {
          gbps: individual_compressed_gbps,
          mbps: individual_compressed_mbps,
          mbitsps: individual_compressed_mbitsps,
          storagePerMinute: individual_compressed_storagePerMinute,
          storagePerHour: individual_compressed_storagePerHour,
          storagePerEvent: individual_compressed_storagePerEvent
        }
      },
      total: {
        uncompressed: {
          gbps: total_gbps,
          mbps: total_mbps,
          mbitsps: total_mbitsps,
          storagePerMinute: total_storagePerMinute,
          storagePerHour: total_storagePerHour,
          storagePerEvent: total_storagePerEvent,
          storageAllEvents: total_storageAllEvents
        },
        compressed: {
          gbps: total_compressed_gbps,
          mbps: total_compressed_mbps,
          mbitsps: total_compressed_mbitsps,
          storagePerMinute: total_compressed_storagePerMinute,
          storagePerHour: total_compressed_storagePerHour,
          storagePerEvent: total_compressed_storagePerEvent,
          storageAllEvents: total_compressed_storageAllEvents
        }
      }
    });
  };

  useEffect(() => {
    calculateDataRate();
  }, [xSize, ySize, frameRate, videoSource, compression, numSources, eventDuration, numEvents]);

  // Fetch SSD recommendations from backend (debounced)
  useEffect(() => {
    const x = parseInt(xSize) || 0;
    const y = parseInt(ySize) || 0;
    const fps = parseFloat(frameRate) || 0;
    const sources = parseInt(numSources) || 0;
    const duration = parseInt(eventDuration) || 0;
    const events = parseInt(numEvents) || 0;
    if (x <= 0 || y <= 0 || fps <= 0 || sources <= 0 || duration <= 0 || events <= 0) {
      setSsdRecommendations([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/calculate-multi-source-data-rate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            x_size: x, y_size: y, frame_rate: fps,
            video_source: videoSource, compression: compression,
            num_sources: sources, event_duration: duration, num_events: events,
            allowed_form_factors: allowedFormFactors.length > 0 ? allowedFormFactors : ['U.2', 'U.3', 'M.2'],
            write_threshold_percent: writeThreshold,
            capacity_utilization_percent: capacityUtilization,
          }),
        });
        if (!res.ok) return;
        const data = await res.json();
        setSsdRecommendations(data.ssd_recommendations || []);
        setRecordingMbpsPerSource(data.recording_data_rate_mbps_per_source || 0);
        setRecordingTotalStorageGb(data.recording_total_storage_gb || 0);
        setUsingCompressedForRecording(!!data.using_compressed_for_recording);
      } catch (err) {
        // silent — calculator still works locally without recommendations
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [xSize, ySize, frameRate, videoSource, compression, numSources, eventDuration, numEvents, allowedFormFactors, writeThreshold, capacityUtilization]);

  const handlePresetChange = (preset) => {
    if (preset.x && preset.y) {
      setXSize(preset.x.toString());
      setYSize(preset.y.toString());
    }
  };

  const handleDurationPreset = (duration) => {
    setEventDuration(duration.toString());
  };

  const formatNumber = (num, decimals = 3) => {
    if (num === 0) return '0';
    if (num < 0.001) return num.toExponential(2);
    return num.toFixed(decimals);
  };

  const formatLargeNumber = (num, unit = 'GB') => {
    if (num === 0) return `0 ${unit}`;
    if (num >= 1024) {
      return `${formatNumber(num / 1024)} T${unit.substring(1)}`;
    }
    return `${formatNumber(num)} ${unit}`;
  };

  const getCurrentBitDepth = () => {
    const sourceOption = videoSourceOptions.find(opt => opt.value === videoSource);
    return sourceOption ? sourceOption.bits : 24;
  };

  const getCurrentCompressionRatio = () => {
    const compressionOption = compressionOptions.find(opt => opt.value === compression);
    return compressionOption ? compressionOption.ratio : 1;
  };

  const toggleFormFactor = (ff) => {
    setAllowedFormFactors((prev) => {
      if (prev.includes(ff)) {
        const next = prev.filter((v) => v !== ff);
        return next.length === 0 ? prev : next; // never allow empty
      }
      return [...prev, ff];
    });
  };

  const buildExportSummary = () => {
    const compressionLabel = compressionOptions.find(o => o.value === compression)?.label || compression;
    const sourceLabel = videoSourceOptions.find(o => o.value === videoSource)?.label || videoSource;
    return [
      ['Resolution', `${xSize} x ${ySize}`],
      ['Frame Rate', `${frameRate} fps`],
      ['Video Source', sourceLabel],
      ['Compression', compressionLabel],
      ['Number of Sources', numSources],
      ['Event Duration', `${eventDuration} s (${(eventDuration / 3600).toFixed(2)} hr)`],
      ['Number of Events', numEvents],
      ['Form Factors Considered', allowedFormFactors.join(', ')],
      ['Write Threshold', `${writeThreshold}%`],
      ['Capacity Utilization', `${capacityUtilization}%`],
      ['Recording Rate / Source', `${formatNumber(recordingMbpsPerSource, 2)} MB/s ${usingCompressedForRecording ? '(compressed)' : '(uncompressed)'}`],
      ['Total Storage (all events)', formatLargeNumber(recordingTotalStorageGb)],
    ];
  };

  const exportCSV = () => {
    const summary = buildExportSummary();
    const lines = [];
    lines.push('Multi-Source Data Rate Calculator - Report');
    lines.push(`Generated,${new Date().toISOString()}`);
    lines.push('');
    lines.push('Configuration');
    summary.forEach(([k, v]) => lines.push(`${k},"${v}"`));
    lines.push('');
    const best = ssdRecommendations.find(r => r.is_best_fit);
    if (best) {
      lines.push('Best Fit');
      lines.push(`Model,"${best.model}"`);
      lines.push(`Form Factor,${best.form_factor}`);
      lines.push(`Capacity (TB),${best.capacity_tb}`);
      lines.push(`Sequential Write (MB/s),${best.sequential_write_mbps}`);
      lines.push(`Write Limit (MB/s) at ${writeThreshold}%,${best.max_usable_write_mbps}`);
      lines.push(`Layout,"${best.layout}"`);
      lines.push(`RAID 0 drives per source,${best.raid0_drives_per_source}`);
      lines.push(`Drives Needed,${best.drives_needed}`);
      lines.push(`Total Array Capacity (TB),${best.total_array_capacity_tb}`);
      lines.push('');
    }
    lines.push('All Recommendations');
    lines.push(`Model,Form Factor,Capacity (TB),Seq. Write (MB/s),Write Limit (MB/s) at ${writeThreshold}%,Layout,RAID0 drives/source,Sources/Drive,Drives by Speed,Drives by Capacity,Drives Needed,Array Capacity (TB),Notes`);
    ssdRecommendations.forEach((r) => {
      const row = [
        `"${r.model}"`, r.form_factor, r.capacity_tb, r.sequential_write_mbps,
        r.max_usable_write_mbps, `"${r.layout}"`, r.raid0_drives_per_source,
        r.sources_per_drive, r.drives_needed_by_speed,
        r.drives_needed_by_capacity, r.drives_needed, r.total_array_capacity_tb,
        `"${r.notes.replace(/"/g, "'")}"`,
      ];
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `data-rate-report-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    doc.setFontSize(16);
    doc.text('Multi-Source Data Rate Calculator - Report', 40, 40);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 56);
    doc.setTextColor(0);

    autoTable(doc, {
      startY: 70,
      head: [['Configuration', 'Value']],
      body: buildExportSummary(),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 64, 175] },
      theme: 'striped',
    });

    const best = ssdRecommendations.find(r => r.is_best_fit);
    if (best) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 14,
        head: [['Best Fit Drive', '']],
        body: [
          ['Model', best.model],
          ['Form Factor', best.form_factor],
          ['Capacity', `${best.capacity_tb} TB`],
          ['Sequential Write', `${best.sequential_write_mbps} MB/s`],
          ['Write Limit', `${formatNumber(best.max_usable_write_mbps, 0)} MB/s (${writeThreshold}%)`],
          ['Layout', best.layout],
          ['RAID 0 drives per source', best.raid0_drives_per_source],
          ['Drives Needed', best.drives_needed],
          ['Total Array Capacity', `${formatNumber(best.total_array_capacity_tb, 2)} TB`],
          ['Notes', best.notes],
        ],
        styles: { fontSize: 9 },
        headStyles: { fillColor: [16, 185, 129] },
        theme: 'striped',
      });
    }

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 14,
      head: [['Drive', 'Form', 'Cap (TB)', 'Seq Wr (MB/s)', `${writeThreshold}% (MB/s)`, 'Layout', 'Drives', 'Array (TB)', 'Notes']],
      body: ssdRecommendations.map((r) => [
        r.model, r.form_factor, r.capacity_tb, r.sequential_write_mbps,
        formatNumber(r.max_usable_write_mbps, 0), r.layout,
        r.drives_needed, formatNumber(r.total_array_capacity_tb, 2), r.notes,
      ]),
      styles: { fontSize: 7.5, cellPadding: 3 },
      headStyles: { fillColor: [219, 39, 119] },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const rec = ssdRecommendations[data.row.index];
          if (rec && rec.is_best_fit) {
            data.cell.styles.fillColor = [209, 250, 229];
            data.cell.styles.fontStyle = 'bold';
          } else if (rec && rec.raid0_drives_per_source === 0) {
            data.cell.styles.textColor = [180, 180, 180];
          }
        }
      },
      theme: 'grid',
    });

    doc.save(`data-rate-report-${Date.now()}.pdf`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_f1924b42-9c85-49c6-a142-9e795b8a44a9/artifacts/1m08y07e_Logotype_BLU.png"
            alt="Systematic Vision"
            className="mx-auto mb-4 h-10 md:h-12 w-auto"
            style={{ filter: 'brightness(0) invert(1)' }}
            data-testid="header-logo"
          />
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Multi-Camera System Calculator
          </h1>
          <p className="text-gray-300 text-lg">
            Calculate bandwidth and storage requirements for multi-camera systems
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid xl:grid-cols-3 lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <h2 className="text-2xl font-semibold mb-6 text-blue-400">Input Parameters</h2>
            
            {/* Presets */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Resolution Presets
              </label>
              <div className="grid grid-cols-1 gap-2">
                {presets.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => handlePresetChange(preset)}
                    className="p-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors border border-gray-600"
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Resolution Inputs */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  X-Size (Width)
                </label>
                <input
                  type="number"
                  value={xSize}
                  onChange={(e) => setXSize(e.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1920"
                  min="1"
                />
                <p className="text-xs text-gray-400 mt-1">pixels</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Y-Size (Height)
                </label>
                <input
                  type="number"
                  value={ySize}
                  onChange={(e) => setYSize(e.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1080"
                  min="1"
                />
                <p className="text-xs text-gray-400 mt-1">pixels</p>
              </div>
            </div>

            {/* Frame Rate */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Frame Rate
              </label>
              <input
                type="number"
                step="0.1"
                value={frameRate}
                onChange={(e) => setFrameRate(e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="30"
                min="0.1"
              />
              <p className="text-xs text-gray-400 mt-1">frames per second (fps)</p>
            </div>

            {/* Video Source Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Video Source Type
              </label>
              <select
                value={videoSource}
                onChange={(e) => setVideoSource(e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {videoSourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">{getCurrentBitDepth()} bits per pixel</p>
            </div>

            {/* Compression */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Compression
              </label>
              <select
                value={compression}
                onChange={(e) => setCompression(e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {compressionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {getCurrentCompressionRatio()}:1 compression ratio
              </p>
            </div>

            {/* System Parameters */}
            <div className="border-t border-gray-600 pt-6 mt-6">
              <h3 className="text-lg font-semibold text-cyan-400 mb-4">System Configuration</h3>
              
              {/* Number of Sources */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Number of Video Sources
                </label>
                <input
                  type="number"
                  value={numSources}
                  onChange={(e) => setNumSources(e.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="1"
                  min="1"
                />
                <p className="text-xs text-gray-400 mt-1">cameras/sources</p>
              </div>

              {/* Event Duration */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Duration Presets
                </label>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {durationPresets.map((preset, index) => (
                    <button
                      key={index}
                      onClick={() => handleDurationPreset(preset.value)}
                      className="p-2 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors border border-gray-600"
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Event Duration
                </label>
                <input
                  type="number"
                  value={eventDuration}
                  onChange={(e) => setEventDuration(e.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="3600"
                  min="1"
                />
                <p className="text-xs text-gray-400 mt-1">seconds ({Math.round(eventDuration / 3600 * 100) / 100} hours)</p>
              </div>

              {/* Number of Events */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Number of Events
                </label>
                <input
                  type="number"
                  value={numEvents}
                  onChange={(e) => setNumEvents(e.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="1"
                  min="1"
                />
                <p className="text-xs text-gray-400 mt-1">total events planned</p>
              </div>
            </div>

            {/* Calculation Info */}
            <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700/50">
              <h3 className="text-sm font-semibold text-blue-300 mb-2">System Overview</h3>
              <div className="text-xs text-gray-300 space-y-1">
                <p><span className="text-blue-300">Resolution:</span> {xSize} × {ySize} @ {frameRate}fps</p>
                <p><span className="text-blue-300">Video Type:</span> {videoSourceOptions.find(o => o.value === videoSource)?.label} ({getCurrentBitDepth()} bpp)</p>
                <p><span className="text-blue-300">Compression:</span> {compression === 'none' ? 'Uncompressed' : compression.toUpperCase()}</p>
                <p><span className="text-blue-300">Sources:</span> {numSources} camera{numSources > 1 ? 's' : ''}</p>
                <p><span className="text-blue-300">Event Duration:</span> {Math.round(eventDuration / 3600 * 100) / 100} hours</p>
                <p><span className="text-blue-300">Total Events:</span> {numEvents}</p>
              </div>
            </div>
          </div>

          {/* Individual Source Results */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <h2 className="text-2xl font-semibold mb-6 text-green-400">Per Source Results</h2>
            
            {/* Uncompressed Results */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-300 mb-3">Uncompressed (Raw)</h3>
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-green-900/50 to-blue-900/50 rounded-lg p-3 border border-green-700/50">
                  <div className="flex justify-between items-center">
                    <span className="text-green-300 text-sm font-medium">Data Rate</span>
                    <span className="text-lg font-bold text-green-400">
                      {formatNumber(results.individual.uncompressed.gbps)} GB/s
                    </span>
                  </div>
                </div>

                <div className="bg-gray-700/50 rounded-lg p-2 border border-gray-600">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-xs">Bandwidth</span>
                    <span className="text-sm font-semibold text-white">
                      {formatNumber(results.individual.uncompressed.mbitsps)} Mbps
                    </span>
                  </div>
                </div>

                <div className="bg-gray-700/50 rounded-lg p-2 border border-gray-600">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-xs">Per Hour</span>
                    <span className="text-sm font-semibold text-white">
                      {formatLargeNumber(results.individual.uncompressed.storagePerHour)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Compressed Results */}
            {compression !== 'none' && (
              <div className="border-t border-gray-600 pt-4">
                <h3 className="text-lg font-semibold text-purple-400 mb-3">Compressed ({compression.toUpperCase()})</h3>
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg p-3 border border-purple-700/50">
                    <div className="flex justify-between items-center">
                      <span className="text-purple-300 text-sm font-medium">Data Rate</span>
                      <span className="text-lg font-bold text-purple-400">
                        {formatNumber(results.individual.compressed.gbps)} GB/s
                      </span>
                    </div>
                    <div className="text-xs text-purple-200/70 mt-1">
                      {getCurrentCompressionRatio()}:1 compression
                    </div>
                  </div>

                  <div className="bg-gray-700/50 rounded-lg p-2 border border-gray-600">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300 text-xs">Bandwidth</span>
                      <span className="text-sm font-semibold text-white">
                        {formatNumber(results.individual.compressed.mbitsps)} Mbps
                      </span>
                    </div>
                  </div>

                  <div className="bg-gray-700/50 rounded-lg p-2 border border-gray-600">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300 text-xs">Per Hour</span>
                      <span className="text-sm font-semibold text-white">
                        {formatLargeNumber(results.individual.compressed.storagePerHour)}
                      </span>
                    </div>
                  </div>

                  <div className="bg-green-900/30 rounded-lg p-2 border border-green-700/50">
                    <div className="text-center">
                      <span className="text-green-300 text-xs">Savings: </span>
                      <span className="text-green-400 font-semibold text-sm">
                        {formatNumber((1 - 1/getCurrentCompressionRatio()) * 100, 1)}% reduction
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Total System Results */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 xl:col-span-1 lg:col-span-2 xl:col-span-1">
            <h2 className="text-2xl font-semibold mb-6 text-orange-400">Total System Results</h2>
            
            {/* Total Uncompressed */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-300 mb-3">Uncompressed Total</h3>
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-orange-900/50 to-red-900/50 rounded-lg p-3 border border-orange-700/50">
                  <div className="flex justify-between items-center">
                    <span className="text-orange-300 font-medium text-sm">Total Data Rate</span>
                    <span className="text-lg font-bold text-orange-400">
                      {formatNumber(results.total.uncompressed.gbps)} GB/s
                    </span>
                  </div>
                  <p className="text-xs text-orange-200/70 mt-1">{numSources} sources combined</p>
                </div>

                <div className="bg-gray-700/50 rounded-lg p-2 border border-gray-600">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-xs">Total Bandwidth</span>
                    <span className="text-sm font-semibold text-white">
                      {formatNumber(results.total.uncompressed.mbitsps)} Mbps
                    </span>
                  </div>
                </div>

                <div className="bg-red-900/30 rounded-lg p-2 border border-red-700/50">
                  <div className="flex justify-between items-center">
                    <span className="text-red-300 text-xs">All Events Storage</span>
                    <span className="text-sm font-semibold text-white">
                      {formatLargeNumber(results.total.uncompressed.storageAllEvents)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Compressed */}
            {compression !== 'none' && (
              <div className="border-t border-gray-600 pt-4">
                <h3 className="text-lg font-semibold text-cyan-400 mb-3">Compressed Total ({compression.toUpperCase()})</h3>
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 rounded-lg p-3 border border-cyan-700/50">
                    <div className="flex justify-between items-center">
                      <span className="text-cyan-300 font-medium text-sm">Total Data Rate</span>
                      <span className="text-lg font-bold text-cyan-400">
                        {formatNumber(results.total.compressed.gbps)} GB/s
                      </span>
                    </div>
                    <p className="text-xs text-cyan-200/70 mt-1">{numSources} sources × {getCurrentCompressionRatio()}:1</p>
                  </div>

                  <div className="bg-gray-700/50 rounded-lg p-2 border border-gray-600">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300 text-xs">Total Bandwidth</span>
                      <span className="text-sm font-semibold text-white">
                        {formatNumber(results.total.compressed.mbitsps)} Mbps
                      </span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-900/50 to-teal-900/50 rounded-lg p-3 border border-green-700/50">
                    <div className="flex justify-between items-center">
                      <span className="text-green-300 font-medium text-sm">All Events Storage</span>
                      <span className="text-lg font-bold text-green-400">
                        {formatLargeNumber(results.total.compressed.storageAllEvents)}
                      </span>
                    </div>
                    <p className="text-xs text-green-200/70 mt-1">{numEvents} events × {numSources} sources</p>
                  </div>

                  <div className="bg-yellow-900/30 rounded-lg p-2 border border-yellow-700/50 text-center">
                    <span className="text-yellow-300 text-xs">Total Savings: </span>
                    <span className="text-yellow-400 font-semibold">
                      {formatLargeNumber(results.total.uncompressed.storageAllEvents - results.total.compressed.storageAllEvents)} saved
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Infrastructure Notes */}
            <div className="mt-6 bg-blue-900/30 rounded-lg p-4 border border-blue-700/50">
              <h4 className="text-sm font-semibold text-blue-300 mb-2">Infrastructure Requirements</h4>
              <ul className="text-xs text-gray-300 space-y-1">
                <li>• Network: {formatNumber(compression === 'none' ? results.total.uncompressed.mbitsps : results.total.compressed.mbitsps)} Mbps total</li>
                <li>• Storage: {formatLargeNumber(compression === 'none' ? results.total.uncompressed.storageAllEvents : results.total.compressed.storageAllEvents)} for all events</li>
                <li>• Per Source: {formatNumber(compression === 'none' ? results.individual.uncompressed.mbitsps : results.individual.compressed.mbitsps)} Mbps each</li>
                <li>• Recommended: Add 20-30% overhead for buffering</li>
              </ul>
            </div>
          </div>
        </div>

        {/* NVMe SSD Recommendations */}
        <div className="max-w-7xl mx-auto mt-6 bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700" data-testid="ssd-recommendations-panel">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-4 gap-3">
            <div>
              <h2 className="text-2xl font-semibold text-pink-400">Recommended U.2 / U.3 / M.2 NVMe SSDs</h2>
              <p className="text-xs text-gray-400 mt-1">
                Synchronized recording from {numSources} source{numSources > 1 ? 's' : ''} • each at{' '}
                <span className="text-pink-300 font-semibold">{formatNumber(recordingMbpsPerSource, 2)} MB/s</span>
                {' '}({usingCompressedForRecording ? `H.264 ${compression.replace('h264_', '').toUpperCase()}` : 'uncompressed'})
                {' • '}total dataset:{' '}
                <span className="text-pink-300 font-semibold">{formatLargeNumber(recordingTotalStorageGb)}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Constraint: per-drive write rate ≤{' '}
                <span className="text-gray-300 font-semibold">{writeThreshold}%</span> of seq write,
                {' '}using{' '}
                <span className="text-gray-300 font-semibold">{capacityUtilization}%</span> of drive capacity.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-gray-300">
                  <span className="uppercase tracking-wide text-gray-400">Write speed %:</span>
                  <select
                    value={writeThreshold}
                    onChange={(e) => setWriteThreshold(parseInt(e.target.value))}
                    data-testid="write-threshold-select"
                    className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-white focus:ring-2 focus:ring-pink-500"
                  >
                    {[25, 50, 75, 100].map((v) => (
                      <option key={v} value={v}>{v}%</option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-300">
                  <span className="uppercase tracking-wide text-gray-400">Capacity %:</span>
                  <select
                    value={capacityUtilization}
                    onChange={(e) => setCapacityUtilization(parseInt(e.target.value))}
                    data-testid="capacity-utilization-select"
                    className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-white focus:ring-2 focus:ring-pink-500"
                  >
                    {[25, 50, 75, 100].map((v) => (
                      <option key={v} value={v}>{v}%</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2" data-testid="form-factor-filter">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Form factors:</span>
                  {['U.2', 'U.3', 'M.2'].map((ff) => {
                    const active = allowedFormFactors.includes(ff);
                    return (
                      <button
                        key={ff}
                        type="button"
                        onClick={() => toggleFormFactor(ff)}
                        data-testid={`ff-toggle-${ff}`}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                          active
                            ? 'bg-pink-500/20 border-pink-400 text-pink-200'
                            : 'bg-gray-700/50 border-gray-600 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {ff}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={exportCSV}
                    disabled={ssdRecommendations.length === 0}
                    data-testid="export-csv-btn"
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-blue-500/20 border border-blue-400 text-blue-200 hover:bg-blue-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={exportPDF}
                    disabled={ssdRecommendations.length === 0}
                    data-testid="export-pdf-btn"
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-rose-500/20 border border-rose-400 text-rose-200 hover:bg-rose-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Export PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          {ssdRecommendations.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Enter valid inputs to view drive recommendations.</p>
          ) : (
            <>
              {(() => {
                const best = ssdRecommendations.find(r => r.is_best_fit);
                if (!best) return null;
                return (
                  <div
                    className="mb-5 rounded-xl p-4 border border-emerald-500/60 bg-gradient-to-r from-emerald-900/40 via-teal-900/30 to-emerald-900/40 shadow-lg shadow-emerald-900/30"
                    data-testid="ssd-best-fit-card"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-400 text-emerald-950 uppercase tracking-wide">
                            Best Fit
                          </span>
                          <span className="text-emerald-300 text-xs">
                            fewest drives · least over-provisioning
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-white mt-2" data-testid="ssd-best-fit-model">
                          {best.model}
                        </h3>
                        <p className="text-xs text-gray-300 mt-1">
                          <span className={`px-2 py-0.5 rounded text-xs mr-2 ${
                            best.form_factor === 'U.2' ? 'bg-blue-900/60 text-blue-300'
                            : best.form_factor === 'U.3' ? 'bg-purple-900/60 text-purple-300'
                            : 'bg-amber-900/60 text-amber-300'
                          }`}>
                            {best.form_factor}
                          </span>
                          {best.capacity_tb} TB · {formatNumber(best.sequential_write_mbps, 0)} MB/s seq write · {writeThreshold}% limit {formatNumber(best.max_usable_write_mbps, 0)} MB/s
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-3 md:gap-5 text-center">
                        <div>
                          <div className="text-3xl font-extrabold text-emerald-300" data-testid="ssd-best-fit-drives">
                            {best.drives_needed}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-emerald-200/80">drives needed</div>
                        </div>
                        <div>
                          {best.raid0_drives_per_source > 1 ? (
                            <>
                              <div className="text-2xl font-bold text-rose-300">
                                RAID 0 × {best.raid0_drives_per_source}
                              </div>
                              <div className="text-[10px] uppercase tracking-wider text-gray-400">per source</div>
                            </>
                          ) : (
                            <>
                              <div className="text-2xl font-bold text-white">
                                {best.sources_per_drive}
                              </div>
                              <div className="text-[10px] uppercase tracking-wider text-gray-400">src / drive</div>
                            </>
                          )}
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-white">
                            {formatNumber(best.total_array_capacity_tb, 2)} TB
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-gray-400">array capacity</div>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-300 mt-3">{best.notes}</p>
                  </div>
                );
              })()}

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="ssd-recommendations-table">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="py-2 pr-4">Drive</th>
                      <th className="py-2 pr-4">Form</th>
                      <th className="py-2 pr-4">Capacity</th>
                      <th className="py-2 pr-4">Seq. Write</th>
                      <th className="py-2 pr-4">{writeThreshold}% Limit</th>
                      <th className="py-2 pr-4">Layout</th>
                      <th className="py-2 pr-4">Drives Needed</th>
                      <th className="py-2 pr-4">Array Capacity</th>
                      <th className="py-2 pr-4">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ssdRecommendations.map((rec, idx) => {
                      const unsuitable = rec.raid0_drives_per_source === 0 || rec.drives_needed === 0;
                      const isRaid = rec.raid0_drives_per_source > 1;
                      const rowClass = unsuitable
                        ? 'opacity-50'
                        : rec.is_best_fit
                          ? 'bg-emerald-900/30 hover:bg-emerald-900/40 ring-1 ring-emerald-500/40'
                          : 'hover:bg-gray-700/40';
                      return (
                        <tr
                          key={idx}
                          className={`border-b border-gray-800 ${rowClass}`}
                          data-testid={`ssd-row-${idx}`}
                        >
                          <td className="py-2 pr-4 font-medium text-white">
                            {rec.is_best_fit && (
                              <span
                                className="mr-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-400 text-emerald-950 align-middle"
                                data-testid={`ssd-best-fit-badge-${idx}`}
                              >
                                BEST
                              </span>
                            )}
                            {rec.model}
                          </td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              rec.form_factor === 'U.2' ? 'bg-blue-900/60 text-blue-300'
                              : rec.form_factor === 'U.3' ? 'bg-purple-900/60 text-purple-300'
                              : 'bg-amber-900/60 text-amber-300'
                            }`}>
                              {rec.form_factor}
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-gray-200">{rec.capacity_tb} TB</td>
                          <td className="py-2 pr-4 text-gray-200">{formatNumber(rec.sequential_write_mbps, 0)} MB/s</td>
                          <td className="py-2 pr-4 text-gray-300">{formatNumber(rec.max_usable_write_mbps, 0)} MB/s</td>
                          <td className="py-2 pr-4 text-gray-200">
                            {unsuitable ? (
                              <span className="text-red-400">—</span>
                            ) : isRaid ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/30 text-rose-200 border border-rose-400/40">
                                  RAID 0
                                </span>
                                <span className="text-rose-200 text-xs">
                                  ×{rec.raid0_drives_per_source} per source
                                </span>
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">{rec.layout}</span>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            {unsuitable ? (
                              <span className="text-red-400">N/A</span>
                            ) : (
                              <span className={`font-bold text-base ${rec.is_best_fit ? 'text-emerald-300' : 'text-pink-400'}`}>
                                {rec.drives_needed}
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-gray-200">
                            {unsuitable ? '—' : `${formatNumber(rec.total_array_capacity_tb, 2)} TB`}
                          </td>
                          <td className="py-2 pr-4 text-xs text-gray-400">{rec.notes}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="text-center mt-8 text-gray-400 text-sm">
          <p>Perfect for surveillance systems, live events, broadcast planning, and multi-camera installations with compression analysis</p>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <DataRateCalculator />
    </div>
  );
}

export default App;